import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3 } from 'lucide-react'
import type { Property } from '@/types/entities'
import {
  SectionCard, EmptyState, Chip, DataTable, type Column,
} from '@/components/ui'
import { C } from '@/lib/colors'
import { money, num, pct } from '@/lib/format'
import type { AccountDerived } from '../lib'
import { latestRevenue, latestOcc, locStr } from '../lib'

type MetricId = 'occupancy' | 'revenue' | 'nrsf' | 'units'
interface MetricDef {
  id: MetricId
  label: string
  value: (p: Property) => number | undefined
  fmt: (v: number | undefined) => string
  color: string
}

const METRICS: MetricDef[] = [
  { id: 'occupancy', label: 'Occupancy', value: latestOcc, fmt: (v) => pct(v), color: C.blue60 },
  { id: 'revenue', label: 'In-Place Revenue', value: latestRevenue, fmt: (v) => money(v), color: C.cyan },
  { id: 'nrsf', label: 'NRSF', value: (p) => p.nrsf, fmt: (v) => num(v), color: C.blue },
  { id: 'units', label: 'Total Units', value: (p) => p.totalUnits, fmt: (v) => num(v), color: C.navy75 },
]

export default function VitalFactorsTab({ d }: { d: AccountDerived }) {
  const navigate = useNavigate()
  const [metricId, setMetricId] = useState<MetricId>('occupancy')
  const metric = METRICS.find((m) => m.id === metricId)!

  if (!d.properties.length) {
    return (
      <SectionCard pad>
        <EmptyState icon={<BarChart3 size={36} />} title="No properties to rank" hint="The Vital Factors scorecard ranks this account's properties by a selectable operating metric." />
      </SectionCard>
    )
  }

  const ranked = [...d.properties]
    .map((p) => ({ p, v: metric.value(p) }))
    .sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity))
  const max = Math.max(...ranked.map((r) => r.v ?? 0), 1)

  const columns: Column<{ p: Property; v: number | undefined }>[] = [
    {
      key: 'rank', header: '#', width: 44, align: 'center',
      render: (_r) => null, // filled via index below using render trick
    },
    {
      key: 'name', header: 'Property',
      render: ({ p }) => (
        <div className="aw-cell-stack">
          <span className="aw-link">{p.name}</span>
          <span className="aw-cell-sub">{locStr(p)} · {p.externalIds?.lcode ?? '—'}</span>
        </div>
      ),
    },
    {
      key: 'bar', header: metric.label, width: 280,
      render: ({ v }) => (
        <div className="aw-vf-barwrap">
          <div className="aw-vf-track">
            <div className="aw-vf-bar" style={{ width: `${((v ?? 0) / max) * 100}%`, background: metric.color }} />
          </div>
        </div>
      ),
    },
    {
      key: 'val', header: 'Value', align: 'right', width: 130,
      render: ({ v }) => <span className="num aw-vf-val">{metric.fmt(v)}</span>,
    },
  ]

  // Inject rank numbers by wrapping the rank render to use array index.
  columns[0].render = (row) => {
    const idx = ranked.indexOf(row)
    return <span className="aw-vf-rank num">{idx + 1}</span>
  }

  return (
    <SectionCard
      eyebrow="Scorecard"
      title={<>Vital <b>Factors</b></>}
      right={<span className="aw-count-pill">{ranked.length} ranked</span>}
    >
      <div className="aw-chip-row">
        <span className="label aw-chip-row-label">Rank by</span>
        {METRICS.map((m) => (
          <Chip key={m.id} active={m.id === metricId} onClick={() => setMetricId(m.id)}>{m.label}</Chip>
        ))}
      </div>
      <DataTable<{ p: Property; v: number | undefined }>
        columns={columns}
        rows={ranked}
        getRowKey={(r) => r.p.id}
        onRowClick={(r) => navigate(`/properties/${r.p.id}`)}
        dense
      />
    </SectionCard>
  )
}
